/**
 * useProfileSave Hook
 *
 * Custom hook for handling profile save logic
 * Manages validation, mutations, and save status
 *
 * Key features:
 * - Categories can be saved independently from location
 * - Location validation only when location fields are changed
 * - User-friendly error messages
 */

import { useState } from 'react';
import { z } from 'zod';
import type { ProfileFormData } from './useProfileForm';
import type { CategoryWithSubcategories } from './useCategoryManagement';
import type { ContractorProfile } from '@hummii/shared-types';
import type { Service } from '../../../api';
import type { ClientProfileResponse } from '../../../api/clientProfile';

// Base validation schema - always required
const baseProfileSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(50, 'First name must be less than 50 characters'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(50, 'Last name must be less than 50 characters'),
  email: z.string().email('Invalid email address'),
  phone: z
    .string()
    .refine(
      (val) => {
        if (!val || val === '' || val === '+') return true;
        return /^\+[1-9]\d{6,14}$/.test(val);
      },
      { message: 'Phone must be a valid international number in E.164 format (e.g. +11234567890)' }
    )
    .optional(),
});

// Location validation schema - only when location is being updated
// Note: Only coordinates are required, address fields are optional
const locationSchema = z.object({
  city: z
    .string()
    .max(100, 'City must be less than 100 characters')
    .optional(),
  province: z
    .string()
    .length(2, 'Province must be a 2-letter code (e.g., ON, QC)')
    .regex(/^[A-Z]{2}$/, 'Province must be uppercase letters (e.g., ON, QC)')
    .optional()
    .or(z.literal('')),
  latitude: z
    .number()
    .min(-90)
    .max(90)
    .nullable()
    .refine((val) => val !== null, {
      message: 'Please select your location using the location picker',
    }),
  longitude: z
    .number()
    .min(-180)
    .max(180)
    .nullable()
    .refine((val) => val !== null, {
      message: 'Please select your location using the location picker',
    }),
  serviceArea: z
    .string()
    .min(1, 'Service area is required')
    .regex(/^\d+(\.\d+)?\s*km$/, { message: 'Service area must be in format "XX km"' }),
});

export interface UseProfileSaveOptions {
  currentUser?: any;
  profileData?: ContractorProfile | null;
  clientProfileData?: ClientProfileResponse | null;
  isContractorMode: boolean;
  updateUser: any;
  updateActor: any;
  updateProfile: any;
  updateLocation: any;
  uploadAvatar: any;
  assignCategories: any;
  createService: any;
  updateService: any;
  deleteService: any;
  myServices?: Service[];
  availableCategories?: any[];
  // Client-specific mutations
  updateClientProfile?: any;
  updateClientLocation?: any;
  onAvatarSuccess?: (avatarUrl: string) => void;
}

export type SaveStatus = 'idle' | 'success' | 'error' | 'partial';

export interface SaveResult {
  success: boolean;
  savedItems: string[];
  warnings: string[];
}

export function useProfileSave({
  currentUser,
  profileData,
  clientProfileData,
  isContractorMode,
  updateUser,
  updateActor,
  updateProfile,
  updateLocation,
  uploadAvatar,
  assignCategories,
  createService,
  updateService,
  deleteService,
  myServices,
  availableCategories,
  updateClientProfile,
  updateClientLocation,
  onAvatarSuccess,
}: UseProfileSaveOptions) {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [locationWarning, setLocationWarning] = useState<string | null>(null);

  /**
   * Scrolls to the field with validation error and highlights it
   */
  const scrollToErrorField = (fieldName: string) => {
    const field =
      document.querySelector(`[name="${fieldName}"]`) ||
      document.querySelector(`[data-field="${fieldName}"]`) ||
      document.querySelector(`#field-${fieldName}`) ||
      document.querySelector(`label:has(~ input[name="${fieldName}"])`);

    if (field) {
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const inputElement = field.closest('.form-field') || field.parentElement;
      if (inputElement) {
        inputElement.classList.add('shake-animation');
        setTimeout(() => inputElement.classList.remove('shake-animation'), 600);
      }
    }
  };

  /**
   * Convert validation errors to user-friendly messages
   */
  const getUserFriendlyError = (field: string, message: string): string => {
    const fieldNames: Record<string, string> = {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone Number',
      city: 'City',
      province: 'Province',
      latitude: 'Location',
      longitude: 'Location',
      serviceArea: 'Service Area',
      profileDescription: 'Profile Description',
      hourlyRate: 'Hourly Rate',
    };

    const friendlyFieldName = fieldNames[field] || field;

    // Return cleaner messages
    if (message.includes('too_small') || message.includes('required')) {
      return `${friendlyFieldName} is required`;
    }
    if (message.includes('Coordinates')) {
      return 'Please select your location using the location picker';
    }
    return message;
  };

  /**
   * Check if location has any data filled
   */
  const hasLocationData = (formData: ProfileFormData): boolean => {
    return !!(
      formData.city ||
      formData.province ||
      formData.latitude !== null ||
      formData.longitude !== null
    );
  };

  /**
   * Check if location data has changed from profile
   */
  const hasLocationChanged = (formData: ProfileFormData): boolean => {
    if (!profileData) return hasLocationData(formData);

    const serviceRadiusNum = parseFloat(formData.serviceArea.replace(/[^\d.]/g, ''));
    return (
      formData.address !== (profileData.address || '') ||
      formData.city !== (profileData.city || '') ||
      formData.province !== (profileData.province || '') ||
      formData.postalCode !== (profileData.postalCode || '') ||
      formData.latitude !== profileData.latitude ||
      formData.longitude !== profileData.longitude ||
      serviceRadiusNum !== (profileData.serviceRadius || 20)
    );
  };

  const handleSave = async (
    formData: ProfileFormData,
    selectedCategoryGroups: CategoryWithSubcategories[],
    setValidationErrors: (errors: Record<string, string>) => void
  ) => {
    const savedItems: string[] = [];
    const warnings: string[] = [];

    try {
      setSaveStatus('idle');
      setValidationErrors({});
      setLocationWarning(null);

      // Check authentication
      if (!currentUser) {
        setSaveStatus('error');
        alert('You are not logged in. Please log in and try again.');
        return;
      }

      // Step 1: Validate base fields (always required)
      const baseValidation = baseProfileSchema.safeParse({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
      });

      if (!baseValidation.success) {
        const errors: Record<string, string> = {};
        baseValidation.error.issues.forEach((issue) => {
          const field = issue.path[0] as string;
          errors[field] = getUserFriendlyError(field, issue.message);
        });
        setValidationErrors(errors);
        setSaveStatus('error');
        scrollToErrorField(Object.keys(errors)[0]);
        return;
      }

      // Step 2: Update Actor profile (displayName, phone)
      const fullDisplayName = `${formData.firstName} ${formData.lastName}`.trim();
      const currentDisplayName = (profileData?.user as any)?.displayName || profileData?.user?.name || '';
      // @ts-ignore - phone exists in full User type
      const currentActorPhone = profileData?.user?.phone || '';
      const actorDataChanged = fullDisplayName !== currentDisplayName || formData.phone !== currentActorPhone;

      if (actorDataChanged) {
        const phoneToSend = formData.phone && formData.phone !== '+' ? formData.phone : undefined;
        await updateActor.mutateAsync({ name: fullDisplayName, displayName: fullDisplayName, phone: phoneToSend });
        savedItems.push('Profile name');
      }

      // Contractor-specific updates
      if (isContractorMode && profileData) {
        // Step 3: Update contractor profile fields (bio, hourlyRate, showLocation)
        const contractorUpdates: any = {};
        if (formData.profileDescription !== (profileData.bio || '')) {
          contractorUpdates.bio = formData.profileDescription;
        }
        if (formData.hourlyRate && parseFloat(formData.hourlyRate) !== profileData.hourlyRate) {
          contractorUpdates.hourlyRate = parseFloat(formData.hourlyRate);
        }
        // @ts-ignore - showLocation will be added to type
        if (formData.showLocation !== (profileData.showLocation || false)) {
          contractorUpdates.showLocation = formData.showLocation;
        }

        if (Object.keys(contractorUpdates).length > 0) {
          await updateProfile.mutateAsync(contractorUpdates);
          savedItems.push('Profile details');
        }

        // Step 4: Update location (only if changed and valid)
        if (hasLocationChanged(formData)) {
          // Only validate location if user is trying to update it
          const locationValidation = locationSchema.safeParse({
            city: formData.city,
            province: formData.province?.toUpperCase(),
            latitude: formData.latitude,
            longitude: formData.longitude,
            serviceArea: formData.serviceArea,
          });

          if (locationValidation.success) {
            const serviceRadiusNum = parseFloat(formData.serviceArea.replace(/[^\d.]/g, ''));
            await updateLocation.mutateAsync({
              latitude: formData.latitude,
              longitude: formData.longitude,
              address: formData.address || undefined,
              city: formData.city || undefined,
              province: formData.province?.toUpperCase() || undefined,
              postalCode: formData.postalCode || undefined,
              serviceRadius: serviceRadiusNum,
            });
            savedItems.push('Location');
          } else {
            // Location validation failed - add warning but continue saving other data
            const locationErrors = locationValidation.error.issues
              .map((issue) => getUserFriendlyError(issue.path[0] as string, issue.message))
              .filter((msg, i, arr) => arr.indexOf(msg) === i); // Remove duplicates

            warnings.push(`Location not saved: ${locationErrors.join(', ')}`);
            setLocationWarning(locationErrors.join('. '));
          }
        } else if (!hasLocationData(formData)) {
          // No location set at all - show persistent warning
          setLocationWarning('Your profile will not appear in search results until you set your location.');
        }

        // Step 5: Update categories and services (independent of location)
        const categoriesChanged = hasCategoriesChanged(selectedCategoryGroups);
        if (categoriesChanged) {
          await updateCategoriesAndServices(selectedCategoryGroups);
          savedItems.push('Categories & Services');
        }
      }

      // Client-specific updates
      if (!isContractorMode && clientProfileData && updateClientProfile && updateClientLocation) {
        const clientUpdates: Record<string, unknown> = {};
        if (formData.profileDescription !== (clientProfileData.bio || '')) {
          clientUpdates.bio = formData.profileDescription;
        }
        if (formData.showLocation !== (clientProfileData.showLocation || false)) {
          clientUpdates.showLocation = formData.showLocation;
        }
        if (Object.keys(clientUpdates).length > 0) {
          await updateClientProfile.mutateAsync(clientUpdates);
          savedItems.push('Profile details');
        }

        // Client location update (similar logic)
        const clientLocationChanged = hasClientLocationChanged(formData);
        if (clientLocationChanged && formData.latitude !== null && formData.longitude !== null) {
          const serviceRadiusNum = parseFloat(formData.serviceArea.replace(/[^\d.]/g, ''));
          await updateClientLocation.mutateAsync({
            latitude: formData.latitude,
            longitude: formData.longitude,
            address: formData.address || undefined,
            city: formData.city || undefined,
            province: formData.province?.toUpperCase() || undefined,
            postalCode: formData.postalCode || undefined,
            serviceRadius: serviceRadiusNum,
          });
          savedItems.push('Location');
        }
      }

      // Upload avatar if selected
      if (formData.selectedAvatar) {
        const result = await uploadAvatar.mutateAsync(formData.selectedAvatar);
        savedItems.push('Avatar');
        
        // Call success callback to update form state
        if (onAvatarSuccess && result?.avatarUrl) {
          onAvatarSuccess(result.avatarUrl);
        }
      }

      // Set final status
      if (warnings.length > 0 && savedItems.length > 0) {
        setSaveStatus('partial');
      } else if (savedItems.length > 0) {
        setSaveStatus('success');
      } else {
        setSaveStatus('success'); // No changes, but no errors
      }

      setTimeout(() => setSaveStatus('idle'), 3000);

    } catch (error: any) {
      console.error('[useProfileSave] Save failed:', error);
      setSaveStatus('error');

      let errorMessage = 'An unexpected error occurred';
      if (error?.response?.message) {
        errorMessage = Array.isArray(error.response.message)
          ? error.response.message.join(', ')
          : error.response.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // Show what was saved before the error
      if (savedItems.length > 0) {
        alert(`Saved: ${savedItems.join(', ')}\n\nError: ${errorMessage}`);
      } else {
        alert(`Failed to save: ${errorMessage}`);
      }

      setTimeout(() => setSaveStatus('idle'), 5000);
    }
  };

  /**
   * Check if client location has changed
   */
  const hasClientLocationChanged = (formData: ProfileFormData): boolean => {
    if (!clientProfileData) return hasLocationData(formData);

    const serviceRadiusNum = parseFloat(formData.serviceArea.replace(/[^\d.]/g, ''));
    return (
      formData.address !== (clientProfileData.address || '') ||
      formData.city !== (clientProfileData.city || '') ||
      formData.province !== (clientProfileData.province || '') ||
      formData.postalCode !== (clientProfileData.postalCode || '') ||
      formData.latitude !== clientProfileData.latitude ||
      formData.longitude !== clientProfileData.longitude ||
      serviceRadiusNum !== (clientProfileData.notificationRadius || 50)
    );
  };

  /**
   * Check if categories have changed (simple check - can be enhanced)
   */
  const hasCategoriesChanged = (selectedCategoryGroups: CategoryWithSubcategories[]): boolean => {
    // For now, always update categories if there are any selected
    // TODO: Compare with existing categories for optimization
    return selectedCategoryGroups.some(group =>
      group.categoryId || group.subcategories.length > 0
    );
  };

  const updateCategoriesAndServices = async (
    selectedCategoryGroups: CategoryWithSubcategories[]
  ) => {
    const allCategoryIds: string[] = [];
    const serviceUpdates: Array<{
      subcategoryId: string;
      price: string;
      unit: string;
    }> = [];

    selectedCategoryGroups.forEach((group) => {
      if (group.categoryId) {
        allCategoryIds.push(group.categoryId);
      }
      group.subcategories.forEach((sub) => {
        if (sub.subcategoryId) {
          allCategoryIds.push(sub.subcategoryId);
          if (sub.price) {
            serviceUpdates.push({
              subcategoryId: sub.subcategoryId,
              price: sub.price,
              unit: sub.unit,
            });
          }
        }
      });
    });

    // Update categories assignment
    await assignCategories.mutateAsync({ categoryIds: allCategoryIds });

    // Delete old services not in new list
    if (myServices) {
      const newSubcategoryIds = serviceUpdates.map((s) => s.subcategoryId);
      for (const service of myServices) {
        if (!newSubcategoryIds.includes(service.categoryId)) {
          await deleteService.mutateAsync(service.id);
        }
      }
    }

    // Create or update services
    for (const serviceUpdate of serviceUpdates) {
      const existingService = myServices?.find(
        (s) => s.categoryId === serviceUpdate.subcategoryId
      );
      const category = availableCategories?.find(
        (c) => c.id === serviceUpdate.subcategoryId
      );

      if (existingService) {
        await updateService.mutateAsync({
          serviceId: existingService.id,
          dto: {
            price: parseFloat(serviceUpdate.price),
            unit: serviceUpdate.unit,
          },
        });
      } else {
        await createService.mutateAsync({
          categoryId: serviceUpdate.subcategoryId,
          name: category?.nameEn || 'Service',
          price: parseFloat(serviceUpdate.price),
          unit: serviceUpdate.unit,
        });
      }
    }
  };

  const isSaving =
    updateUser?.isPending ||
    updateActor.isPending ||
    updateProfile.isPending ||
    updateLocation.isPending ||
    uploadAvatar.isPending ||
    assignCategories.isPending ||
    createService.isPending ||
    updateService.isPending;

  const clearLocationWarning = () => setLocationWarning(null);

  return {
    handleSave,
    saveStatus,
    isSaving,
    locationWarning,
    clearLocationWarning,
  };
}
